/**
 * Global client-side error reporting.
 *
 * Catches *all* truly-unhandled browser exceptions — uncaught sync errors,
 * unhandled promise rejections, and (via ErrorBoundary) React render errors —
 * and POSTs them to /api/client-errors so we can collect + diagnose them.
 * Explicitly handled setup failures can use reportClientError too.
 * "These should not happen", so any report is a signal worth keeping.
 *
 * Coexists with the garbage suppressors in client/index.html: those run in the
 * capture phase and drop Firefox noise + non-Error events before our
 * bubble-phase listeners fire. We re-apply the same ignore list here as defense
 * in depth. The reporter never throws and never loops (it swallows its own
 * failures), dedupes repeated signatures, and caps total sends per page load.
 */
import { detectAppMode } from "@/lib/app-routing";
import { detectEnv } from "@/lib/env";

export type ClientErrorType = "error" | "unhandledrejection" | "react";

export interface ReportInput {
  type: ClientErrorType;
  message: string;
  stack?: string | null;
  componentStack?: string | null;
  source?: string | null;
  lineno?: number | null;
  colno?: number | null;
  /** Caller-supplied diagnostics; avoid secrets and raw request/response data. */
  context?: Record<string, string | number | boolean | null>;
}

const ENDPOINT = "/api/client-errors";
const MAX_REPORTS = 25; // per page load — backstop against an error storm
const MAX_STACK = 12000; // chars; server truncates further, keeps body < keepalive's 64KB cap

// Same patterns client/index.html suppresses — never report this noise.
const IGNORE = ["__firefox__", "is not an object", "evaluating", "unknown runtime error"];

let installed = false;
let sentCount = 0;
const seen = new Set<string>();

// First real stack frame (skip the message line) — used for dedup signatures.
function firstStackFrame(stack?: string | null): string {
  if (!stack) return "";
  const lines = stack.split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.find((l) => l.startsWith("at ")) ?? lines[1] ?? lines[0] ?? "";
}

/** Report a single error. Safe to call from anywhere — it can never throw. */
export function reportClientError(input: ReportInput): void {
  try {
    const message = String(input.message ?? "");
    const lower = message.toLowerCase();
    if (IGNORE.some((p) => lower.includes(p))) return;

    const sig = `${input.type}|${message}|${firstStackFrame(input.stack)}`;
    if (seen.has(sig) || sentCount >= MAX_REPORTS) return;
    seen.add(sig);
    sentCount += 1;

    const body = JSON.stringify({
      type: input.type,
      message: message.slice(0, MAX_STACK),
      stack: input.stack ? input.stack.slice(0, MAX_STACK) : undefined,
      componentStack: input.componentStack ? input.componentStack.slice(0, MAX_STACK) : undefined,
      source: input.source ?? undefined,
      lineno: input.lineno ?? undefined,
      colno: input.colno ?? undefined,
      url: window.location.href,
      appMode: detectAppMode(),
      env: detectEnv(),
      userAgent: navigator.userAgent,
      clientTime: new Date().toISOString(),
      context: { ...input.context, pathname: window.location.pathname },
    });

    // keepalive: the POST survives an imminent unload/navigation.
    // credentials:'omit': public endpoint, no cookies needed.
    // .catch(): swallow everything — a failed report must not surface as a new error.
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    /* the reporter must never throw — that would re-enter the handlers and loop */
  }
}

/** Install the global window listeners. Idempotent; call once at startup. */
export function installErrorReporter(): void {
  if (installed) return;
  installed = true;

  // Bubble phase (default) so index.html's capture-phase garbage filter runs first.
  window.addEventListener("error", (e: ErrorEvent) => {
    reportClientError({
      type: "error",
      message: e.message || e.error?.message || "Unknown error",
      stack: e.error?.stack ?? null,
      source: e.filename ?? null,
      lineno: e.lineno ?? null,
      colno: e.colno ?? null,
    });
  });

  window.addEventListener("unhandledrejection", (e: PromiseRejectionEvent) => {
    const reason: unknown = e.reason;
    let message: string;
    let stack: string | null = null;
    if (reason instanceof Error) {
      message = reason.message;
      stack = reason.stack ?? null;
    } else if (typeof reason === "string") {
      message = reason;
    } else {
      try {
        message = JSON.stringify(reason);
      } catch {
        message = String(reason);
      }
    }
    reportClientError({
      type: "unhandledrejection",
      message: message || "Unhandled promise rejection",
      stack,
    });
  });
}
